import { Button } from '@carbon/react';
import { Download } from '@carbon/react/icons';
import ErrorToast from 'components/error-toast';
import {
  useExportByProperty,
  useExportByPropertyFailure,
  useIsExportingByProperty,
} from '../../hooks';
import { rescatOption } from '../../utils';

/**
 * Export by property (INS-12790): downloads the run's slot changes as one CSV — a row per
 * property, a column per factor — scoped to the reserve category the shown results carry. The
 * server builds the file and names it; the export hook saves it.
 *
 * While the file is prepared the button reads "Preparing…" and ignores clicks — also after the
 * user leaves Results and comes back, since the export keeps running. It stays enabled, so
 * keyboard focus stays on it, and is marked aria-disabled so assistive technology knows it's
 * inactive. A failure stands as a toast until dismissed (without the server's reason, which can
 * name internals) — also one that landed after the user left, or for another category, which the
 * toast then names — and the button can be used again.
 *
 * @param {object} props - The button's props.
 * @param {number} props.runId - The comparison run to export.
 * @param {string | null} props.rescat - The reserve category of the results on screen (null = all).
 * @param {boolean} [props.disabled] - There is nothing to export yet (the run is still
 *   calculating, a category switch is loading, or the results are empty).
 * @return {JSX.Element} The button, with its error toast after a failed export.
 */
export function PropertyExportButton({
  runId,
  rescat,
  disabled = false,
}: {
  runId: number;
  rescat: string | null;
  disabled?: boolean;
}) {
  const exportByProperty = useExportByProperty();
  const busy = useIsExportingByProperty(runId, rescat);
  const { failure, dismiss } = useExportByPropertyFailure(runId);

  /** Starts the export, unless one is already being prepared. */
  const onClick = () => {
    if (busy) {
      return;
    }
    exportByProperty.mutate({ runId, rescat });
  };

  return (
    <>
      {failure && (
        <ErrorToast
          // A toast hides itself once closed: the next failure needs a fresh one.
          key={`${runId}:${failure.rescat}`}
          message={
            failure.rescat === rescat
              ? 'The export could not be built. Try again.'
              : `The ${rescatOption(failure.rescat).label} export could not be built. Try again.`
          }
          timeout={0}
          onClose={() => {
            dismiss();
            return true;
          }}
        />
      )}
      <Button
        kind="ghost"
        size="sm"
        renderIcon={Download}
        disabled={disabled}
        aria-disabled={busy || undefined}
        onClick={onClick}
      >
        {busy ? 'Preparing…' : 'Export by property'}
      </Button>
    </>
  );
}
